import React, { useState, useMemo, useCallback } from 'react';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

const monoStyle = { fontFamily: "'IBM Plex Mono', monospace" };
const serifStyle = { fontFamily: "'DM Serif Display', serif" };

const AMOUNT_PRESETS = [
  { label: '$1k', value: 1000 },
  { label: '$5k', value: 5000 },
  { label: '$10k', value: 10000 },
  { label: '$50k', value: 50000 },
  { label: '$100k', value: 100000 },
];

const PERIOD_PRESETS = [
  { label: '1 month', value: 1 },
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 },
  { label: '1 year', value: 12 },
  { label: '2 years', value: 24 },
];

const COMPOUND_OPTIONS = ['daily', 'weekly', 'monthly', 'none'];

function compoundFreqPerYear(freq) {
  if (freq === 'daily') return 365;
  if (freq === 'weekly') return 52;
  if (freq === 'monthly') return 12;
  return 1; // none = simple interest
}

function calcFinalValue(principal, aprPct, months, compounding) {
  const apr = aprPct / 100;
  const years = months / 12;
  if (compounding === 'none') {
    return principal * (1 + apr * years);
  }
  const n = compoundFreqPerYear(compounding);
  return principal * Math.pow(1 + apr / n, n * years);
}

function calcSimpleEarnings(principal, aprPct, months) {
  return principal * (aprPct / 100) * (months / 12);
}

function buildMonthlyBreakdown(principal, aprPct, months, compounding) {
  const rows = [];
  const apr = aprPct / 100;
  let balance = principal;

  for (let m = 1; m <= months; m++) {
    const startBalance = balance;
    let endBalance;
    if (compounding === 'none') {
      const monthlyEarning = principal * apr / 12;
      endBalance = startBalance + monthlyEarning;
    } else {
      const n = compoundFreqPerYear(compounding);
      const periodsThisMonth = n / 12;
      endBalance = startBalance * Math.pow(1 + apr / n, periodsThisMonth);
    }
    const earnings = endBalance - startBalance;
    rows.push({ month: m, start: startBalance, earnings, end: endBalance });
    balance = endBalance;
  }
  return rows;
}

function buildChartData(principal, aprPct, months, compounding) {
  const points = [];
  const totalPoints = Math.min(months * 4, 200);
  const apr = aprPct / 100;

  for (let i = 0; i <= totalPoints; i++) {
    const frac = i / totalPoints;
    const currentMonths = frac * months;
    const years = currentMonths / 12;

    // Hold (no yield)
    const hold = principal;

    // LP without compounding (simple interest)
    const lpSimple = principal * (1 + apr * years);

    // LP with compounding
    let lpCompound;
    if (compounding === 'none') {
      lpCompound = lpSimple;
    } else {
      const n = compoundFreqPerYear(compounding);
      lpCompound = principal * Math.pow(1 + apr / n, n * years);
    }

    points.push({ t: currentMonths, hold, lpSimple, lpCompound });
  }
  return points;
}

function buildScenarioData(principal, aprPct, months, compounding, apyPct30D) {
  // Weight scenarios based on 30D trend
  let optimisticMult = 1.20;
  let pessimisticMult = 0.60;

  if (apyPct30D != null) {
    if (apyPct30D > 0) {
      optimisticMult = 1.20 + Math.min(apyPct30D / 100, 0.3);
      pessimisticMult = 0.60 + Math.min(apyPct30D / 200, 0.15);
    } else {
      optimisticMult = 1.20 + Math.max(apyPct30D / 200, -0.15);
      pessimisticMult = 0.60 + Math.max(apyPct30D / 100, -0.2);
    }
  }

  const totalPoints = Math.min(months * 4, 200);
  const points = [];

  for (let i = 0; i <= totalPoints; i++) {
    const frac = i / totalPoints;
    const currentMonths = frac * months;

    const base = calcFinalValue(principal, aprPct, currentMonths, compounding);
    const optimistic = calcFinalValue(principal, aprPct * optimisticMult, currentMonths, compounding);
    const pessimistic = calcFinalValue(principal, aprPct * pessimisticMult, currentMonths, compounding);

    points.push({ t: currentMonths, base, optimistic, pessimistic });
  }
  return points;
}

function formatUsd(n) {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${n.toFixed(2)}`;
}

function SvgAreaChart({ data, keys, lineColors, fillColors, labels, width, height, colors }) {
  if (!data || data.length === 0) return null;

  const padding = { top: 20, right: 20, bottom: 30, left: 60 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  let allVals = [];
  keys.forEach(k => data.forEach(d => allVals.push(d[k])));
  const minVal = Math.min(...allVals) * 0.95;
  const maxVal = Math.max(...allVals) * 1.05;
  const tMin = data[0].t;
  const tMax = data[data.length - 1].t;

  const scaleX = (t) => padding.left + ((t - tMin) / (tMax - tMin || 1)) * chartW;
  const scaleY = (v) => padding.top + chartH - ((v - minVal) / (maxVal - minVal || 1)) * chartH;

  const buildPath = (key) => {
    return data.map((d, i) => {
      const x = scaleX(d.t);
      const y = scaleY(d[key]);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  };

  const buildArea = (key) => {
    const line = data.map((d, i) => {
      const x = scaleX(d.t);
      const y = scaleY(d[key]);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const lastX = scaleX(data[data.length - 1].t);
    const firstX = scaleX(data[0].t);
    const bottomY = padding.top + chartH;
    return `${line} L${lastX.toFixed(1)},${bottomY} L${firstX.toFixed(1)},${bottomY} Z`;
  };

  // Y-axis ticks
  const yTicks = [];
  const tickCount = 5;
  for (let i = 0; i <= tickCount; i++) {
    const val = minVal + (maxVal - minVal) * (i / tickCount);
    yTicks.push(val);
  }

  // X-axis ticks
  const xTicks = [];
  const xTickCount = Math.min(6, Math.ceil(tMax));
  for (let i = 0; i <= xTickCount; i++) {
    const t = tMin + (tMax - tMin) * (i / xTickCount);
    xTicks.push(t);
  }

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {yTicks.map((val, i) => (
        <line key={`yg${i}`} x1={padding.left} y1={scaleY(val)} x2={padding.left + chartW} y2={scaleY(val)}
          stroke={colors.border} strokeWidth={0.5} strokeDasharray="3,3" />
      ))}

      {/* Areas */}
      {keys.map((key, i) => fillColors[i] ? (
        <path key={`area-${key}`} d={buildArea(key)} fill={fillColors[i]} />
      ) : null)}

      {/* Lines */}
      {keys.map((key, i) => (
        <path key={`line-${key}`} d={buildPath(key)} fill="none" stroke={lineColors[i]}
          strokeWidth={2} strokeLinecap="round" />
      ))}

      {/* Y labels */}
      {yTicks.map((val, i) => (
        <text key={`yl${i}`} x={padding.left - 8} y={scaleY(val) + 4}
          textAnchor="end" fill={colors.labelMuted} fontSize={9}
          fontFamily="'IBM Plex Mono', monospace">
          {formatUsd(val)}
        </text>
      ))}

      {/* X labels */}
      {xTicks.map((t, i) => (
        <text key={`xl${i}`} x={scaleX(t)} y={padding.top + chartH + 18}
          textAnchor="middle" fill={colors.labelMuted} fontSize={9}
          fontFamily="'IBM Plex Mono', monospace">
          {t < 1 ? `${(t * 30).toFixed(0)}d` : `${t.toFixed(0)}mo`}
        </text>
      ))}

      {/* Legend */}
      {labels.map((label, i) => (
        <g key={`leg${i}`} transform={`translate(${padding.left + i * 130}, ${height - 4})`}>
          <line x1={0} y1={-4} x2={16} y2={-4} stroke={lineColors[i]} strokeWidth={2} />
          <text x={20} y={0} fill={colors.textSecondary} fontSize={9}
            fontFamily="'IBM Plex Mono', monospace">{label}</text>
        </g>
      ))}
    </svg>
  );
}

export default function FarmCalculatorView({ pools = [] }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [investment, setInvestment] = useState(10000);
  const [months, setMonths] = useState(12);
  const [compounding, setCompounding] = useState('monthly');

  const filteredPools = useMemo(() => {
    if (!searchTerm) return pools.slice(0, 50);
    const q = searchTerm.toLowerCase();
    return pools.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.protocol.toLowerCase().includes(q) ||
      p.chain.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [pools, searchTerm]);

  const selectedPool = useMemo(() => {
    if (!selectedPoolId) return null;
    return pools.find(p => String(p.id) === String(selectedPoolId)) || null;
  }, [selectedPoolId, pools]);

  const apr = selectedPool ? selectedPool.apr : 0;

  const projections = useMemo(() => {
    if (!selectedPool || investment <= 0) return null;
    const simpleEarnings = calcSimpleEarnings(investment, apr, months);
    const compoundedValue = calcFinalValue(investment, apr, months, compounding);
    const compoundedEarnings = compoundedValue - investment;
    const monthlyBreakdown = buildMonthlyBreakdown(investment, apr, months, compounding);
    const chartData = buildChartData(investment, apr, months, compounding);
    const scenarioData = buildScenarioData(investment, apr, months, compounding, selectedPool.apyPct30D);

    return {
      simpleEarnings,
      compoundedEarnings,
      compoundedValue,
      monthlyBreakdown,
      chartData,
      scenarioData,
    };
  }, [selectedPool, investment, apr, months, compounding]);

  const handleSelectPool = useCallback((pool) => {
    setSelectedPoolId(pool.id);
    setSearchTerm(pool.name);
    setDropdownOpen(false);
  }, []);

  const cardStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: isMobile ? 16 : 24,
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    ...monoStyle,
    fontSize: 11,
    color: colors.labelMuted,
    letterSpacing: 0.5,
    fontWeight: 500,
    marginBottom: 8,
    textTransform: 'uppercase',
  };

  const inputStyle = {
    ...monoStyle,
    fontSize: 14,
    color: colors.textPrimary,
    background: colors.inputBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    padding: '10px 14px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  const pillStyle = (active) => ({
    ...monoStyle,
    fontSize: 11,
    fontWeight: active ? 600 : 500,
    color: active ? '#FFFFFF' : colors.textSecondary,
    background: active ? colors.green : colors.badgeBg,
    border: `1px solid ${active ? colors.green : colors.border}`,
    borderRadius: 20,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  const periodLabel = months === 1 ? '1 month' : months < 12 ? `${months} months` : months === 12 ? '1 year' : `${(months / 12).toFixed(1)} years`;

  return (
    <div style={{ padding: isMobile ? '16px 16px 60px' : '24px 24px 60px', maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          ...serifStyle,
          fontSize: 28,
          color: colors.textPrimary,
          margin: '0 0 6px 0',
          fontWeight: 400,
        }}>
          Farm Calculator
        </h1>
        <p style={{
          ...monoStyle,
          fontSize: 12,
          color: colors.labelMuted,
          margin: 0,
        }}>
          Estimate farming returns with compounding projections and risk scenarios
        </p>
      </div>

      {/* Pool Selector */}
      <div style={{ ...cardStyle, marginBottom: 16, position: 'relative' }}>
        <div style={labelStyle}>Select Pool</div>
        <input
          type="text"
          placeholder="Search pools by name, protocol, or chain..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setDropdownOpen(true);
            if (!e.target.value) setSelectedPoolId('');
          }}
          onFocus={() => setDropdownOpen(true)}
          style={inputStyle}
        />
        {dropdownOpen && filteredPools.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 100,
            boxShadow: `0 8px 24px ${colors.shadow}`,
            marginTop: -8,
          }}>
            {filteredPools.map(pool => (
              <div
                key={pool.id}
                onClick={() => handleSelectPool(pool)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: `1px solid ${colors.borderSubtle}`,
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = colors.hover}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <span style={{ ...monoStyle, fontSize: 13, color: colors.textPrimary, fontWeight: 600 }}>
                    {pool.name}
                  </span>
                  <span style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginLeft: 8 }}>
                    {pool.protocol} / {pool.chain}
                  </span>
                </div>
                <span style={{ ...monoStyle, fontSize: 12, color: colors.green, fontWeight: 600 }}>
                  {pool.apr.toFixed(2)}% APR
                </span>
              </div>
            ))}
          </div>
        )}
        {/* Click-away listener */}
        {dropdownOpen && (
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
            onClick={() => setDropdownOpen(false)}
          />
        )}

        {selectedPool && (
          <div style={{
            marginTop: 12,
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
          }}>
            <div style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>
              <span style={{ color: colors.labelMuted }}>APR: </span>
              <span style={{ color: colors.green, fontWeight: 600 }}>{selectedPool.apr.toFixed(2)}%</span>
            </div>
            <div style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>
              <span style={{ color: colors.labelMuted }}>TVL: </span>
              <span style={{ fontWeight: 600 }}>{formatUsd(selectedPool.tvl)}</span>
            </div>
            <div style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>
              <span style={{ color: colors.labelMuted }}>Risk: </span>
              <span style={{ fontWeight: 600 }}>{selectedPool.risk}</span>
            </div>
            {selectedPool.apyPct30D != null && (
              <div style={{ ...monoStyle, fontSize: 11, color: colors.textSecondary }}>
                <span style={{ color: colors.labelMuted }}>30D trend: </span>
                <span style={{
                  fontWeight: 600,
                  color: selectedPool.apyPct30D >= 0 ? colors.green : colors.red,
                }}>
                  {selectedPool.apyPct30D >= 0 ? '+' : ''}{selectedPool.apyPct30D.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Investment Amount */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={labelStyle}>Investment Amount ($)</div>
        <input
          type="number"
          value={investment}
          onChange={e => setInvestment(Number(e.target.value) || 0)}
          style={{ ...inputStyle, marginBottom: 10 }}
          min={0}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {AMOUNT_PRESETS.map(p => (
            <button
              key={p.value}
              onClick={() => setInvestment(p.value)}
              style={pillStyle(investment === p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Time Period */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={labelStyle}>Time Period: {periodLabel}</div>
        <input
          type="range"
          min={1}
          max={24}
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          style={{ width: '100%', marginBottom: 10, accentColor: colors.green }}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PERIOD_PRESETS.map(p => (
            <button
              key={p.value}
              onClick={() => setMonths(p.value)}
              style={pillStyle(months === p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Compounding Frequency */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={labelStyle}>Compounding Frequency</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {COMPOUND_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => setCompounding(opt)}
              style={pillStyle(compounding === opt)}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {projections && selectedPool && (
        <>
          {/* Projection Cards */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ ...cardStyle, flex: 1, minWidth: 200 }}>
              <div style={labelStyle}>Estimated Earnings (Simple)</div>
              <div style={{ ...serifStyle, fontSize: 28, color: colors.green }}>
                {formatUsd(projections.simpleEarnings)}
              </div>
              <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginTop: 4 }}>
                Without compounding
              </div>
            </div>
            <div style={{ ...cardStyle, flex: 1, minWidth: 200 }}>
              <div style={labelStyle}>Estimated Earnings (Compounded)</div>
              <div style={{ ...serifStyle, fontSize: 28, color: colors.green }}>
                {formatUsd(projections.compoundedEarnings)}
              </div>
              <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginTop: 4 }}>
                {compounding !== 'none' ? `${compounding} compounding` : 'No compounding'}
              </div>
            </div>
            <div style={{ ...cardStyle, flex: 1, minWidth: 200 }}>
              <div style={labelStyle}>Final Portfolio Value</div>
              <div style={{ ...serifStyle, fontSize: 28, color: colors.textPrimary }}>
                {formatUsd(projections.compoundedValue)}
              </div>
              <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginTop: 4 }}>
                From {formatUsd(investment)} initial
              </div>
            </div>
          </div>

          {/* Growth Chart */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ ...serifStyle, fontSize: 18, color: colors.textPrimary, marginBottom: 16 }}>
              Portfolio Growth Over Time
            </div>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <SvgAreaChart
                data={projections.chartData}
                keys={['hold', 'lpSimple', 'lpCompound']}
                lineColors={[colors.labelMuted, colors.gold, colors.green]}
                fillColors={['transparent', 'transparent', `${colors.green}15`]}
                labels={['Just Hold', 'LP (Simple)', 'LP (Compounded)']}
                width={880}
                height={300}
                colors={colors}
              />
            </div>
          </div>

          {/* Risk Scenarios */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ ...serifStyle, fontSize: 18, color: colors.textPrimary, marginBottom: 4 }}>
              Risk-Adjusted Scenarios
            </div>
            <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginBottom: 16 }}>
              Optimistic (+20% APR), Base Case, Pessimistic (-40% APR) — weighted by 30D APY trend
            </div>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <SvgAreaChart
                data={projections.scenarioData}
                keys={['optimistic', 'base', 'pessimistic']}
                lineColors={[colors.green, colors.gold, colors.red]}
                fillColors={[`${colors.green}10`, 'transparent', `${colors.red}10`]}
                labels={['Optimistic', 'Base Case', 'Pessimistic']}
                width={880}
                height={280}
                colors={colors}
              />
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
              {[
                { label: 'Optimistic', value: projections.scenarioData[projections.scenarioData.length - 1]?.optimistic, color: colors.green },
                { label: 'Base Case', value: projections.scenarioData[projections.scenarioData.length - 1]?.base, color: colors.gold },
                { label: 'Pessimistic', value: projections.scenarioData[projections.scenarioData.length - 1]?.pessimistic, color: colors.red },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted }}>{s.label}</div>
                  <div style={{ ...serifStyle, fontSize: 20, color: s.color }}>{formatUsd(s.value || 0)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Breakdown Table */}
          <div style={{ ...cardStyle, marginBottom: 16, overflowX: 'auto' }}>
            <div style={{ ...serifStyle, fontSize: 18, color: colors.textPrimary, marginBottom: 16 }}>
              Monthly Breakdown
            </div>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              ...monoStyle,
              fontSize: 12,
            }}>
              <thead>
                <tr>
                  {['Month', 'Starting Balance', 'Earnings', 'Ending Balance'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderBottom: `2px solid ${colors.border}`,
                      color: colors.labelMuted,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projections.monthlyBreakdown.map((row, i) => (
                  <tr key={row.month} style={{
                    background: i % 2 === 0 ? 'transparent' : colors.surfaceAlt,
                  }}>
                    <td style={{ padding: '8px 12px', color: colors.textSecondary }}>
                      {row.month}
                    </td>
                    <td style={{ padding: '8px 12px', color: colors.textPrimary }}>
                      {formatUsd(row.start)}
                    </td>
                    <td style={{ padding: '8px 12px', color: colors.green, fontWeight: 600 }}>
                      +{formatUsd(row.earnings)}
                    </td>
                    <td style={{ padding: '8px 12px', color: colors.textPrimary, fontWeight: 600 }}>
                      {formatUsd(row.end)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Card */}
          <div style={{
            ...cardStyle,
            background: colors.bannerGradient,
            border: `1px solid ${colors.greenBorder}`,
          }}>
            <div style={{
              ...serifStyle,
              fontSize: 18,
              color: colors.textPrimary,
              lineHeight: 1.6,
            }}>
              Investing{' '}
              <span style={{ color: colors.green, fontWeight: 600 }}>{formatUsd(investment)}</span>
              {' '}in{' '}
              <span style={{ color: colors.green, fontWeight: 600 }}>{selectedPool.name}</span>
              {' '}({selectedPool.protocol} / {selectedPool.chain})
              {' '}for{' '}
              <span style={{ color: colors.green, fontWeight: 600 }}>{periodLabel}</span>
              {' '}at current rates would earn approximately{' '}
              <span style={{ color: colors.green, fontWeight: 600 }}>{formatUsd(projections.compoundedEarnings)}</span>
              {compounding !== 'none' ? ` with ${compounding} compounding` : ''}.
            </div>
            <div style={{ ...monoStyle, fontSize: 10, color: colors.labelMuted, marginTop: 10 }}>
              Disclaimer: These projections are estimates based on current APR rates. Actual returns may vary significantly due to market conditions, impermanent loss, and APR fluctuations.
            </div>
          </div>
        </>
      )}

      {!selectedPool && (
        <div style={{
          ...cardStyle,
          textAlign: 'center',
          padding: '60px 24px',
        }}>
          <div style={{ ...serifStyle, fontSize: 22, color: colors.textSecondary, marginBottom: 8 }}>
            Select a pool to begin
          </div>
          <div style={{ ...monoStyle, fontSize: 12, color: colors.labelMuted }}>
            Search and select a pool above to see farming projections
          </div>
        </div>
      )}
    </div>
  );
}
